import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Timer, ArrowRight, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";

const POLL_INTERVAL_MS = 2000;
const MAX_ATTEMPTS = 12; // ≈ 24 seconds

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");

  const [state, setState] = useState("polling"); // polling | paid | failed | expired
  const [data, setData] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const poll = useCallback(async () => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      setData(data);
      if (data.payment_status === "paid") {
        setState("paid");
        try { window.dispatchEvent(new Event("wallet-refresh")); } catch (e) { /* noop */ }
        return;
      }
      if (data.status === "expired") {
        setState("expired");
        return;
      }
      // not paid yet — try again
      setAttempts((a) => a + 1);
    } catch (e) {
      // 404 is possible if Stripe redirect arrives before our DB insert finishes
      // — retry a couple of times, then fail.
      setAttempts((a) => a + 1);
    }
  }, [sessionId]);

  useEffect(() => {
    if (state !== "polling") return;
    if (attempts === 0) {
      poll();
      return;
    }
    if (attempts >= MAX_ATTEMPTS) {
      setState("failed");
      return;
    }
    const t = setTimeout(poll, POLL_INTERVAL_MS);
    return () => clearTimeout(t);
  }, [attempts, state, poll]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-dotted">
      <div className="nv-card max-w-lg w-full p-8 text-center" data-testid="payment-success-card">
        {state === "polling" && (
          <>
            <Loader2 className="w-12 h-12 text-novatus-green mx-auto animate-spin mb-4" />
            <h1 className="font-display font-black text-2xl text-white">Zahlung wird verarbeitet</h1>
            <p className="text-novatus-text-2 mt-2">
              Wir warten kurz auf die Bestätigung von Stripe — meistens nur ein paar Sekunden.
            </p>
          </>
        )}

        {state === "paid" && (
          <>
            <div className="w-16 h-16 rounded-full bg-novatus-green/15 border-2 border-novatus-green flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-novatus-green" />
            </div>
            <h1 className="font-display font-black text-3xl text-white">Zahlung erfolgreich!</h1>
            {data && (
              <p className="text-novatus-text-2 mt-3 text-lg">
                <span className="text-novatus-green font-bold">{data.minutes} Minuten</span>{" "}
                wurden deinem Konto gutgeschrieben.
              </p>
            )}
            <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-novatus-green/50 bg-novatus-green/10 text-novatus-green text-xs font-bold">
              <Timer className="w-3.5 h-3.5" />
              Voice-Training freigeschaltet
            </div>
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <button
                onClick={() => navigate("/training")}
                className="btn-green flex-1 h-11 rounded-lg inline-flex items-center justify-center gap-2"
                data-testid="payment-success-go-train"
              >
                Jetzt trainieren <ArrowRight className="w-4 h-4" />
              </button>
              <Link
                to="/dashboard"
                className="flex-1 h-11 rounded-lg border border-novatus text-novatus-text-2 hover:bg-white/5 inline-flex items-center justify-center"
              >
                Zum Dashboard
              </Link>
            </div>
          </>
        )}

        {state === "expired" && (
          <>
            <div className="w-16 h-16 rounded-full bg-amber-400/15 border-2 border-amber-400 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-amber-300" />
            </div>
            <h1 className="font-display font-black text-2xl text-white">Sitzung abgelaufen</h1>
            <p className="text-novatus-text-2 mt-2">
              Die Stripe-Sitzung ist abgelaufen, bevor die Zahlung abgeschlossen wurde. Es wurde nichts berechnet.
            </p>
            <Link to="/dashboard" className="btn-green mt-6 inline-block h-11 px-5 rounded-lg leading-[44px]">
              Zurück zum Dashboard
            </Link>
          </>
        )}

        {state === "failed" && (
          <>
            <div className="w-16 h-16 rounded-full bg-red-400/15 border-2 border-red-400 flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-8 h-8 text-red-300" />
            </div>
            <h1 className="font-display font-black text-2xl text-white">Status unbekannt</h1>
            <p className="text-novatus-text-2 mt-2">
              Wir konnten den Zahlungsstatus nicht final bestätigen. Prüfe deinen Mail-Eingang oder
              versuche es in wenigen Minuten erneut — falls Stripe die Zahlung bestätigt, wird das
              Guthaben automatisch nachgetragen.
            </p>
            <Link to="/dashboard" className="btn-green mt-6 inline-block h-11 px-5 rounded-lg leading-[44px]">
              Zurück zum Dashboard
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentSuccess;
